import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ShieldAlert, Bell, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useGlobalStats } from '@/hooks/useGlobalStats';
import { useRepositories } from '@/hooks/useRepositories';
import {
  useRecentNotifications,
  useUnreadNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '@/hooks/useNotifications';
import { NotificationRow } from '@/components/notifications/NotificationRow';
import { useNotificationStream } from '@/hooks/useNotificationStream';
import { api } from '@/lib/api';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenuItem,
  SidebarHeader,

} from '@/components/ui/sidebar';

import { Icons } from '@/lib/icons';
import DroneLogoMark from '@/components/icons/DroneLogoMark';
import { useAnalysisRuns } from '@/hooks/useAnalysisRuns';
import { WorkspaceSwitcher } from '@/components/dashboard/WorkspaceSwitcher';
import { UsageMeter } from '../billing/UsageMeter';

import ScanRepositoryModal from '@/components/ScanRepositoryModal';
import { KeyboardShortcutsProvider, useShortcuts } from '@/components/common/KeyboardShortcuts';
import { useNoindex } from '@/hooks/useNoindex';

// Header button to open the shortcuts overlay. Must live inside the provider.
const ShortcutsButton: React.FC = () => {
  const { toggle } = useShortcuts();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      data-kbd="? — shortcuts"
      className="h-8 w-8 text-muted-foreground hover:text-foreground transition-colors"
      title="Keyboard shortcuts (?)"
    >
      <Icons.Help className="h-4 w-4" />
    </Button>
  );
};

// ── Nav badge components ─────────────────────────────────────────────────────
function NavBadge({ count, variant = 'neutral' }: { count: number | string; variant?: 'neutral' | 'danger' | 'lime' }) {
  const styles = {
    neutral: 'bg-[var(--accent,#1e1e24)] border border-[rgba(255,255,255,.08)] text-[#6a6a7a]',
    danger:  'bg-[rgba(224,85,85,.15)] border border-[rgba(224,85,85,.2)] text-[#e05555]',
    lime:    'bg-[rgba(181,232,74,.12)] border border-[rgba(181,232,74,.25)] text-[#b5e84a]',
  };
  return (
    <span className={`text-[10px] font-mono rounded-[4px] px-[5px] py-[1px] leading-none ${styles[variant]}`}>
      {count}
    </span>
  );
}

const DashboardLayout: React.FC = () => {
  // Every authenticated application page is never indexed.
  useNoindex();

  const { user, logout, currentOrganization } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Default to collapsed on mobile (< 768px which is md breakpoint)
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return true;
  });
  const [scanDialogOpen, setScanDialogOpen] = useState(false);

  // Nav badge counts — lightweight, share cache with other consumers
  const { data: globalStats } = useGlobalStats();
  const { data: reposData } = useRepositories({ page: 1, page_size: 1 });
  const { data: scansData } = useAnalysisRuns({}, true);
  const { data: billingUsage } = useQuery({
    queryKey: ['billing-usage', user?.id],
    queryFn: () => api.request<{ plan_tier: string; scan_limit: number; scans_remaining: number }>('/api/billing/usage'),
    enabled: !!user,
    staleTime: 60_000,
  });

  // Notification bell — live count/list, kept fresh over SSE
  const { data: unreadData } = useUnreadNotificationCount();
  const { data: notificationsData } = useRecentNotifications();
  const markNotificationRead = useMarkNotificationRead();
  const markAllNotificationsRead = useMarkAllNotificationsRead();
  useNotificationStream();

  // Open the scan dialog when the global `N` shortcut fires (from KeyboardShortcutsProvider).
  useEffect(() => {
    const onNewScan = () => setScanDialogOpen(true);
    window.addEventListener('debtdrone:new-scan', onNewScan);
    return () => window.removeEventListener('debtdrone:new-scan', onNewScan);
  }, [])

  // Auto-collapse sidebar on mobile when resizing
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Derive readable page name from the current path
  const PAGE_NAMES: Record<string, string> = {
    dashboard: 'Dashboard',
    repositories: 'Repositories',
    advisor: 'Advisor',
    scans: 'Scans',
    issues: 'Issues',
    reports: 'Reports',
    settings: 'Settings',
    billing: 'Billing',
    profile: 'Profile',
    admin: 'Admin',
    docs: 'Docs',
    pricing: 'Pricing',
  };
  const pathSegment = location.pathname.split('/').filter(Boolean)[0] ?? 'dashboard';
  const pageName = PAGE_NAMES[pathSegment] ?? (pathSegment.charAt(0).toUpperCase() + pathSegment.slice(1));

  return (
    <KeyboardShortcutsProvider>
    <div className="flex h-screen overflow-hidden w-full bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 flex h-14 border-b border-hairline bg-background/82 backdrop-blur-[18px]">

        {/* Logo zone — mirrors sidebar width on md+, hidden on mobile */}
        <div className={`hidden md:flex items-center gap-2 flex-shrink-0 px-4 transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-[232px]'}`}>
          <DroneLogoMark className="h-7 w-7" />
          {!sidebarCollapsed && (
            <span className="text-[15px] font-semibold tracking-[-0.3px]">DebtDrone</span>
          )}
        </div>

        {/* Main header area — breadcrumb starts at sidebar edge on md+ */}
        <div className="flex flex-1 items-center gap-[10px] pl-0 pr-4">

          {/* Mobile: hamburger + logo */}
          <button
            className="md:hidden p-2 hover:bg-accent rounded-md transition-colors"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-label="Toggle menu"
          >
            {sidebarCollapsed ? <Icons.ChevronRight className="h-4 w-4" /> : <Icons.ChevronLeft className="h-4 w-4" />}
          </button>
          <div className="flex md:hidden items-center gap-2 flex-shrink-0">
            <DroneLogoMark className="h-7 w-7" />
          </div>

          {/* Breadcrumb — md+ only */}
          <div className="hidden md:flex items-center gap-[10px]">
            <div className="w-px h-[18px] bg-hairline" />
            <nav className="flex items-center gap-[6px] text-[13px]">
              <span className="text-muted-foreground">{currentOrganization?.name ?? '…'}</span>
              <span className="text-muted-foreground">/</span>
              <span className="text-foreground">{pageName}</span>
            </nav>
          </div>

          {/* Right cluster */}
          <div className="ml-auto flex items-center gap-[6px]">

            {/* Shortcuts */}
            <ShortcutsButton />

            {/* Notification bell */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="relative w-[30px] h-[30px] rounded-[6px] grid place-items-center text-muted-foreground hover:bg-accent hover:text-foreground transition-colors outline-none"
                  title="Notifications"
                >
                  <Bell className="h-[14px] w-[14px]" />
                  {!!unreadData?.count && (
                    <span className="absolute top-[1px] right-[1px] min-w-[13px] h-[13px] px-[3px] rounded-full bg-sev-critical border-[1.5px] border-background text-white text-[8px] font-bold leading-[10px] text-center pointer-events-none">
                      {unreadData.count > 9 ? '9+' : unreadData.count}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between border-b border-hairline px-3 py-2">
                  <span className="text-[12px] font-semibold">Notifications</span>
                  {!!unreadData?.count && (
                    <button
                      type="button"
                      onClick={() => markAllNotificationsRead.mutate()}
                      className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Mark all read
                    </button>
                  )}
                </div>

                <div className="max-h-[300px] overflow-y-auto py-1">
                  {notificationsData?.notifications?.length ? (
                    notificationsData.notifications.map((n) => (
                      <DropdownMenuItem
                        key={n.id}
                        className="whitespace-normal py-2"
                        onClick={() => {
                          if (!n.read_at) markNotificationRead.mutate(n.id);
                          if (n.action_url) navigate(n.action_url);
                        }}
                      >
                        <NotificationRow notification={n} compact />
                      </DropdownMenuItem>
                    ))
                  ) : (
                    <div className="px-2 py-4 text-center text-[12px] text-muted-foreground">
                      No notifications yet
                    </div>
                  )}
                </div>

                <DropdownMenuSeparator className="my-0" />
                <DropdownMenuItem
                  className="justify-center py-2 text-[11px] font-medium"
                  onClick={() => navigate('/notifications')}
                >
                  View all notifications →
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Separator */}
            <div className="w-px h-[18px] bg-hairline mx-1" />

            {/* New scan */}
            <button
              type="button"
              onClick={() => setScanDialogOpen(true)}
              data-kbd="N — new scan"
              title="Scan Repository"
              className="hidden sm:inline-flex items-center gap-[6px] h-[30px] px-3 rounded-[6px] bg-primary text-primary-foreground text-[12px] font-semibold hover:brightness-110 transition-all border-0 cursor-pointer"
            >
              <Icons.Zap className="h-[11px] w-[11px]" />
              New scan
            </button>

            {/* User chip */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-[7px] px-[4px] pr-[8px] py-[3px] rounded-[20px] border border-hairline hover:bg-accent transition-colors cursor-pointer outline-none"
                >
                  <Avatar className="h-[22px] w-[22px] flex-shrink-0">
                    {user?.avatar_url ? <AvatarImage src={user.avatar_url} alt={user?.first_name} /> : null}
                    <AvatarFallback className="text-[9px] font-bold">
                      {getInitials(user?.first_name, user?.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden md:inline text-[12px] font-medium leading-none">
                    {user?.first_name} {user?.last_name?.charAt(0)}.
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="mr-2 h-3.5 w-3.5" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
                  <Icons.Logout className="mr-2 h-3.5 w-3.5" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile overlay */}
      {!sidebarCollapsed && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 md:hidden top-14 transition-opacity duration-300 ease-in-out"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        className={`fixed left-0 top-14 bottom-0 h-auto z-50 md:z-40 border-r border-border bg-background transition-all duration-300 ease-in-out ${sidebarCollapsed ? '-translate-x-full md:translate-x-0' : 'translate-x-0'
          }`}
      >
        <SidebarHeader className="px-3 py-4 h-auto flex-col items-stretch gap-0 relative border-b-0">
          <WorkspaceSwitcher collapsed={sidebarCollapsed} />


          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="rounded-full p-1 bg-background border border-border shadow-sm hover:bg-accent text-muted-foreground hover:text-accent-foreground transition-all hidden md:flex items-center justify-center absolute -right-3 top-6 h-6 w-6 z-50"
          >
            {sidebarCollapsed ? (
              <Icons.ChevronRight className="h-3 w-3" />
            ) : (
              <Icons.ChevronLeft className="h-3 w-3" />
            )}
          </button>
        </SidebarHeader>

        <SidebarContent className="flex flex-col pb-0 px-2">
          {/* Main Navigation Group */}
          <SidebarGroup className="p-0 mt-2">
            <SidebarGroupContent className="gap-0.5 flex flex-col">
              <SidebarMenuItem
                className="h-9 px-3"
                active={isActive('/dashboard')}
                icon={<Icons.Dashboard className="h-4 w-4" />}
                onClick={() => navigate('/dashboard')}
                title={sidebarCollapsed ? 'Dashboard' : undefined}
              >
                {!sidebarCollapsed && <span className="text-sm">Dashboard</span>}
              </SidebarMenuItem>
              <SidebarMenuItem
                className="h-9 px-3"
                active={isActive('/repositories')}
                icon={<Icons.Repositories className="h-4 w-4" />}
                onClick={() => navigate('/repositories')}
                title={sidebarCollapsed ? 'Repositories' : undefined}
                badge={!sidebarCollapsed && reposData?.pagination?.total_items
                  ? <NavBadge count={reposData.pagination.total_items} />
                  : undefined}
              >
                {!sidebarCollapsed && <span className="text-sm">Repositories</span>}
              </SidebarMenuItem>
              <SidebarMenuItem
                className="h-9 px-3"
                active={location.pathname === '/advisor' || location.pathname.startsWith('/advisor/')}
                icon={<Icons.AI className="h-4 w-4" />}
                onClick={() => navigate('/advisor')}
                title={sidebarCollapsed ? 'Advisor' : undefined}
              >
                {!sidebarCollapsed && <span className="text-sm">Advisor</span>}
              </SidebarMenuItem>
              <SidebarMenuItem
                className="h-9 px-3"
                active={isActive('/scans')}
                icon={<Icons.Scans className="h-4 w-4" />}
                onClick={() => navigate('/scans')}
                title={sidebarCollapsed ? 'Scans' : undefined}
                badge={!sidebarCollapsed && scansData?.total
                  ? <NavBadge count={scansData.total} />
                  : undefined}
              >
                {!sidebarCollapsed && <span className="text-sm">Scans</span>}
              </SidebarMenuItem>
              <SidebarMenuItem
                className="h-9 px-3"
                active={isActive('/issues')}
                icon={<Icons.Issues className="h-4 w-4" />}
                onClick={() => navigate('/issues')}
                title={sidebarCollapsed ? 'Issues' : undefined}
                badge={!sidebarCollapsed && globalStats?.total_issues
                  ? <NavBadge count={globalStats.total_issues} variant="danger" />
                  : undefined}
              >
                {!sidebarCollapsed && <span className="text-sm">Issues</span>}
              </SidebarMenuItem>
              <SidebarMenuItem
                className="h-9 px-3"
                active={isActive('/reports')}
                icon={<Icons.Reports className="h-4 w-4" />}
                onClick={() => navigate('/reports')}
                title={sidebarCollapsed ? 'Reports' : undefined}
              >
                {!sidebarCollapsed && <span className="text-sm">Reports</span>}
              </SidebarMenuItem>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Organization Navigation Group */}
          <SidebarGroup className="p-0 mt-6">
            {!sidebarCollapsed && <SidebarGroupLabel className="px-3 mb-2 text-xs font-medium text-muted-foreground/70 tracking-wider">Organization</SidebarGroupLabel>}
            <SidebarGroupContent className="gap-0.5 flex flex-col">
              <SidebarMenuItem
                className="h-9 px-3"
                active={isActive('/settings')}
                icon={<Icons.Settings className="h-4 w-4" />}
                onClick={() => navigate('/settings')}
                title={sidebarCollapsed ? 'Settings' : undefined}
              >
                {!sidebarCollapsed && <span className="text-sm">Settings</span>}
              </SidebarMenuItem>
              <SidebarMenuItem
                className="h-9 px-3"
                active={isActive('/billing')}
                icon={<Icons.Billing className="h-4 w-4" />}
                onClick={() => navigate('/billing')}
                title={sidebarCollapsed ? 'Billing' : undefined}
                badge={!sidebarCollapsed && billingUsage?.plan_tier
                  ? <NavBadge count={billingUsage.plan_tier} variant="lime" />
                  : undefined}
              >
                {!sidebarCollapsed && <span className="text-sm">Billing</span>}
              </SidebarMenuItem>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Admin Navigation Group */}
          {user?.is_admin && (
            <SidebarGroup className="p-0 mt-6">
              {!sidebarCollapsed && <SidebarGroupLabel className="px-3 mb-2 text-xs font-medium text-red-500/70 tracking-wider">Administration</SidebarGroupLabel>}
              <SidebarGroupContent className="gap-0.5 flex flex-col">
                <SidebarMenuItem
                  className="h-9 px-3 text-red-600 hover:text-red-700 hover:bg-red-500/5 dark:hover:bg-red-500/10"
                  active={isActive('/admin')}
                  icon={<ShieldAlert className="h-4 w-4" />}
                  onClick={() => navigate('/admin')}
                  title={sidebarCollapsed ? 'God Mode' : undefined}
                >
                  {!sidebarCollapsed && <span className="text-sm font-medium">God Mode</span>}
                </SidebarMenuItem>
              </SidebarGroupContent>
            </SidebarGroup>
          )}

          {/* Usage Meter - only in expanded mode */}
          {!sidebarCollapsed && (
            <div className="mt-auto px-3 pb-4">
              <UsageMeter />
            </div>
          )}
        </SidebarContent>

      </Sidebar>

      {/* Main Content — mockup page-container: centered, max-w 1280, px-8 py-6 */}
      <main className={`flex-1 overflow-auto pt-14 transition-all duration-300 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-[232px]'
        }`}>
        <div id="app-print-root" className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8">
          <Outlet />
        </div>
      </main>

      <ScanRepositoryModal
        open={scanDialogOpen}
        onOpenChange={setScanDialogOpen}
        onScanComplete={() => {
          // Global scan complete logic if needed
        }}
      />
    </div>
    </KeyboardShortcutsProvider>
  );
};

export default DashboardLayout;
